import { deleteMutator, filePostMutator, postMutator, putMutator } from "@/api/swr/mutator";
import useSWRMutation, { SWRMutationConfiguration } from "swr/mutation";
import { z } from "zod";

type ZSchema = z.AnyZodObject | z.ZodUndefined | z.ZodNull;

type SuccessResponse<Schema extends ZSchema> = {
  message: string;
  data: z.infer<Schema>;
};

type ErrorResponse<Schema extends ZSchema> = {
  message: string;
  errors:
    | {
        [key in keyof z.infer<Schema>]?: string[];
      }
    | undefined;
};

export type Config<
  ReqSchema extends ZSchema,
  ResSchema extends ZSchema,
> = SWRMutationConfiguration<
  SuccessResponse<ResSchema>,
  ErrorResponse<ReqSchema>,
  string,
  z.infer<ReqSchema>
>;

type Return<ReqSchema extends ZSchema, ResSchema extends ZSchema> = ReturnType<
  typeof useSWRMutation<
    SuccessResponse<ResSchema>,
    ErrorResponse<ReqSchema>,
    string,
    z.infer<ReqSchema>
  >
>;

export function usePostMutation<
  ReqSchema extends ZSchema,
  ResSchema extends ZSchema,
>(
  _: ReqSchema,
  responseSchema: ResSchema,
  url: string,
  config?: Config<ReqSchema, ResSchema>,
): Return<ReqSchema, ResSchema> {
  const { data, ...rest } = useSWRMutation<
    SuccessResponse<ResSchema>,
    ErrorResponse<ReqSchema>,
    string,
    z.infer<ReqSchema>
  >(url, postMutator, config);
  if (!data) return { data, ...rest };
  return {
    data: {
      message: data.message,
      data: data.data && responseSchema.parse(data.data),
    },
    ...rest,
  };
}

export function useFilePostMutation<
  ReqSchema extends ZSchema,
  ResSchema extends ZSchema,
>(
  reqSchema: ReqSchema,
  responseSchema: ResSchema,
  url: string,
  config?: Config<ReqSchema, ResSchema>,
): Return<ReqSchema, ResSchema> {
  const { data, ...rest } = useSWRMutation<
    SuccessResponse<ResSchema>,
    ErrorResponse<ReqSchema>,
    string,
    z.infer<ReqSchema>
  >(url, filePostMutator, config);
  if (!data) return { data, ...rest };
  return {
    data: {
      message: data.message,
      data: data.data && responseSchema.parse(data.data),
    },
    ...rest,
  };
}

export function usePutMutation<
  ReqSchema extends ZSchema,
  ResSchema extends ZSchema,
>(
  _: ReqSchema,
  responseSchema: ResSchema,
  url: string,
  config?: Config<ReqSchema, ResSchema>,
): Return<ReqSchema, ResSchema> {
  const { data, ...rest } = useSWRMutation<
    SuccessResponse<ResSchema>,
    ErrorResponse<ReqSchema>,
    string,
    z.infer<ReqSchema>
  >(url, putMutator, config);
  if (!data) return { data, ...rest };
  return {
    data: {
      message: data.message,
      data: data.data && responseSchema.parse(data.data),
    },
    ...rest,
  };
}

export function useDeleteMutation<
  ReqSchema extends ZSchema,
  ResSchema extends ZSchema,
>(
  _: ReqSchema,
  responseSchema: ResSchema,
  url: string,
  config?: Config<ReqSchema, ResSchema>,
): Return<ReqSchema, ResSchema> {
  const { data, ...rest } = useSWRMutation<
    SuccessResponse<ResSchema>,
    ErrorResponse<ReqSchema>,
    string,
    z.infer<ReqSchema>
  >(url, deleteMutator, config);
  if (!data) return { data, ...rest };
  return {
    data: {
      message: data.message,
      data: data.data && responseSchema.parse(data.data),
    },
    ...rest,
  };
}

